// Purchasing › Goods receipts (UC-PURCHASING-008; W9): the receipts of a branch, newest first — received date, then number — with
// their supplier, the order they were against ("No purchase order" in Sprint 4), the reason, lines and units, total ex VAT and who
// received them; the supplier, the received dates, "without an order" and an exact delivery note filter them, kept in the address,
// with the branch picker — the user's branches, All branches for an Owner or Admin (BR-PURCHASING-021). A receipt's record shows it
// exactly as posted, with no control that changes it (BR-PURCHASING-014); its serials open their records (A2), and a movement or a
// serial naming the receipt opens it here (A3). The prototype's Goods receipts tab holds receipts against purchase orders only, so the
// list is designed from the use case in purchasing.html's vocabulary. Also here, for the screens that show receipts beside other
// things: a supplier's receipts on its record (UC-PURCHASING-003 A3) and Settings › Markup & fitting's recent overrides at receipt
// (UC-PRICING-007 A6, settings-markups.html's Recent overrides at purchase).
import { PRICE_HISTORY_PAGE, type FrameMarkupBandView, type PriceHistoryEntryView, type SupplierListItem } from "@visionx/contracts";
import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { pricing } from "../api/endpoints.pricing";
import { goodsReceipts, suppliers } from "../api/endpoints.purchasing";
import { isApiError } from "../api/problem";
import { addDays, formatDate, formatMoney, isLocalDate, localDate } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Loading, Pager, Panel, Select, TextInput } from "../ui/bits";
import { SupplierPicker } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { byLine, markupText, possessive } from "./PricingShared";
import { PurchasingHead, PurchasingTabs } from "./Purchasing";
import { ReceiptBody, ReceiptLink, countsWords, dayWords } from "./ReceiptsShared";
import { shortName, useStockBranch } from "./StockShared";

const PAGE = 50;
const dash = <span className="faint">—</span>;

/** /purchasing/receipts */
export function GoodsReceipts() {
  const s = useSessionView();
  const navigate = useNavigate();
  const allowed = can(s, "purchasing.po.read");
  const br = useStockBranch();
  const [sp, setSp] = useSearchParams();
  const supplierId = sp.get("supplier") ?? undefined;
  const from = isLocalDate(sp.get("from")) ? sp.get("from")! : undefined;
  const to = isLocalDate(sp.get("to")) ? sp.get("to")! : undefined;
  const withoutOrder = sp.get("noOrder") === "true";
  const noteParam = sp.get("note") ?? "";
  const [noteTyped, setNoteTyped] = useState(noteParam);
  useEffect(() => { setNoteTyped(noteParam); }, [noteParam]);
  const backwards = from !== undefined && to !== undefined && to < from;
  const supplierRead = useQuery({ queryKey: ["supplier", supplierId], queryFn: () => suppliers.get(supplierId!), enabled: allowed && supplierId !== undefined, staleTime: 60_000 });
  const supplierValue = supplierId ? { id: supplierId, name: supplierRead.data?.data.name ?? "" } : null;
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const filterKey = [br.branchId, supplierId, from, to, withoutOrder, noteParam].join("|");
  useEffect(() => { setCursors([]); }, [filterKey]);
  const list = useQuery({
    queryKey: ["goods-receipts", "list", br.branchId ?? "", supplierId ?? "", from ?? "", to ?? "", withoutOrder, noteParam, cursor ?? ""],
    queryFn: () => goodsReceipts.list({ branchId: br.branchId && !br.isActing ? br.branchId : undefined, supplierId, from, to, withoutOrder, deliveryNoteNumber: noteParam || undefined }, { limit: PAGE, cursor }),
    enabled: allowed && !backwards && br.branchId !== null, placeholderData: keepPreviousData,
  });
  const setParam = (k: string, v: string | null) => setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const filtered = supplierId !== undefined || from !== undefined || to !== undefined || withoutOrder || noteParam !== "";
  const clear = () => setSp((p) => { const n = new URLSearchParams(); const b = p.get("branch"); if (b) n.set("branch", b); return n; }, { replace: true });
  const rows = list.data?.data.items ?? [];
  const denied = !allowed || (list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED");
  return (
    <section className="screen" id="s-goods-receipts">
      <div className="content">
        <PurchasingHead />
        <PurchasingTabs />
        {denied ? <NoAccessPanel page="Goods receipts" error={list.error} permission="purchasing.po.read" /> : <>
          <div className="toolbar">
            {br.choices.length > 1 && (
              <Select aria-label="Branch" boxClass="w-branch" value={br.branchId ?? ""} onChange={(e) => br.choose(e.target.value)}>
                {br.choices.map((b) => <option key={b.id} value={b.id}>Branch · {b.name}</option>)}
              </Select>
            )}
            <SupplierPicker bare ariaLabel="Supplier" boxClass="w-sel" status="All" placeholder="Supplier · All" value={supplierValue} onPick={(x: SupplierListItem | null) => setParam("supplier", x?.id ?? null)} />
            <TextInput type="date" aria-label="Received from" boxClass="w-date" mono value={from ?? ""} onChange={(e) => setParam("from", e.target.value || null)} />
            <TextInput type="date" aria-label="Received to" boxClass="w-date" mono value={to ?? ""} error={backwards} onChange={(e) => setParam("to", e.target.value || null)} />
            <Checkbox checked={withoutOrder} onChange={(v) => setParam("noOrder", v ? "true" : null)}>Without an order</Checkbox>
            <form className="input w-note" role="search" aria-label="Delivery note" onSubmit={(e) => { e.preventDefault(); setParam("note", noteTyped.trim() || null); }}>
              <input aria-label="Delivery note number" placeholder="Delivery note — exact, Enter" autoComplete="off" maxLength={64} value={noteTyped}
                onChange={(e) => setNoteTyped(e.target.value)} onBlur={() => setParam("note", noteTyped.trim() || null)} />
            </form>
            {filtered && <button className="btn sm" type="button" onClick={clear}>Clear filters</button>}
          </div>
          {backwards && <Alert tone="warn">The last received day is before the first: change one of the dates.</Alert>}
          {list.isPending && !backwards && <Loading what="goods receipts" />}
          {list.isError && !denied && (isApiError(list.error) && list.error.code === "NOT_FOUND" ? <Alert tone="warn"><b>Not found.</b> You may list only the receipts of the branches you are assigned to.</Alert> : <ErrorAlert error={list.error} />)}
          {list.data && !backwards && (
            <Panel tight title="Goods receipts" meta={`${br.isAll ? "every branch" : br.name} · newest first`} foot={<>
              <span className="small faint">A receipt is posted once and never edited · receipts against purchase orders join the list in Sprint 7</span>
              {(cursors.length > 0 || list.data.data.nextCursor) && <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data.data.nextCursor!])} />}
            </>}>
              {rows.length === 0 ? (
                <Empty title={filtered ? "No receipt matches these filters." : `No goods received at ${br.isAll ? "any branch" : br.name} yet.`} actions={filtered ? <button className="btn" type="button" onClick={clear}>Clear filters</button> : null} />
              ) : (
                <div className="tbl-scroll">
                  <table>
                    <thead><tr><th>Receipt</th><th>Received</th>{br.isAll && <th>Branch</th>}<th>Supplier</th><th className="wide-only">Order</th><th>Reason</th><th className="r">Lines</th><th className="r">Total ex VAT</th><th className="wide-only">Received by</th></tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} className="clickable" onClick={() => navigate(`/purchasing/receipts/${r.id}`)}>
                          <td className="mono nw"><Link to={`/purchasing/receipts/${r.id}`} onClick={(e) => e.stopPropagation()}>{r.number}</Link><div className="small faint narrow-only">{r.purchaseOrderId ? r.purchaseOrderId : "no purchase order"}</div></td>
                          <td className="nw">{dayWords(r.receivedDate)}<div className="small faint narrow-only">{r.receivedBy ? shortName(r.receivedBy.displayName) : ""}</div></td>
                          {br.isAll && <td>{r.branch.name}</td>}
                          <td>{r.supplier.name}</td>
                          <td className="nw wide-only">{r.purchaseOrderId ? <span className="mono">{r.purchaseOrderId}</span> : "No purchase order"}</td>
                          <td className="reason">{r.reason ?? dash}{r.deliveryNoteNumber && <div className="small faint">delivery note <span className="mono">{r.deliveryNoteNumber}</span></div>}</td>
                          <td className="r">{countsWords(r.lineCount, r.unitCount)}</td>
                          <td className="money">{formatMoney(r.totalExVatCents)}</td>
                          <td className="nw wide-only">{r.receivedBy ? shortName(r.receivedBy.displayName) : dash}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          )}
        </>}
      </div>
    </section>
  );
}

/** /purchasing/receipts/:id — one receipt as posted; nothing on it can be changed (UC-PURCHASING-008 step 4–5). */
export function GoodsReceiptRecord() {
  const s = useSessionView();
  const { id = "" } = useParams();
  const allowed = can(s, "purchasing.po.read");
  const q = useQuery({ queryKey: ["goods-receipts", "one", id], queryFn: () => goodsReceipts.get(id), enabled: allowed });
  const v = q.data?.data;
  return (
    <section className="screen" id="s-goods-receipt">
      <div className="content">
        <PageHead
          title={v ? <span className="row c gap-8">Receipt <span className="mono">{v.number}</span><Chip tone="ok" dot>Posted</Chip></span> : "Goods receipt"}
          sub={v ? `${v.branch.name} · ${v.supplier.name} · ${v.purchaseOrderId ? "against an order" : "no purchase order"} · received ${dayWords(v.receivedDate)}${v.receivedBy ? ` by ${shortName(v.receivedBy.displayName)}` : ""}` : undefined}
          actions={<Link className="btn" to="/purchasing/receipts">Goods receipts</Link>}
        />
        {!allowed && <NoAccessPanel page="Goods receipts" permission="purchasing.po.read" />}
        {allowed && q.isPending && <Loading what="the receipt" />}
        {q.isError && (isApiError(q.error) && q.error.code === "NOT_FOUND"
          ? <Alert tone="warn"><b>This receipt was not found.</b> It may be another branch&#39;s you are not assigned to, or another organisation&#39;s.</Alert>
          : isApiError(q.error) && q.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Goods receipts" error={q.error} /> : <ErrorAlert error={q.error} />)}
        {v && <ReceiptBody v={v} />}
      </div>
    </section>
  );
}

/** A supplier's receipts on its record, for the roles that read receipts (UC-PURCHASING-003 A3); every branch's for an Owner or Admin. */
export function SupplierReceiptsPanel({ supplierId, supplierName }: { supplierId: string; supplierName: string }) {
  const s = useSessionView();
  // A practice of one store: its receipts are its one branch's, named, never "every branch" (UC-PURCHASING-008 A5; change 13).
  const oneStore = useOneStore();
  const all = (s?.user.role === "Owner" || s?.user.role === "Admin") && !oneStore;
  const branch = s?.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s?.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? "the acting branch";
  const q = useQuery({ queryKey: ["goods-receipts", "of-supplier", supplierId, all], queryFn: () => goodsReceipts.list({ supplierId, branchId: all ? "all" : undefined }, { limit: 10 }) });
  const rows = q.data?.data.items ?? [];
  return (
    <Panel title="Goods receipts" meta={`${all ? "every branch" : branch} · newest first`} tight
      foot={<Link to={`/purchasing/receipts?supplier=${supplierId}${all ? "&branch=all" : ""}`}>All receipts from {supplierName}</Link>}>
      {q.isPending && <div className="panel-body"><Loading what="its receipts" /></div>}
      {q.isError && <div className="panel-body"><ErrorAlert error={q.error} /></div>}
      {q.data && (rows.length === 0 ? <Empty title={`Nothing received from ${supplierName} yet.`} /> : (
        <div className="list">
          {rows.map((r) => (
            <div key={r.id} className="item">
              <div className="grow">
                <div className="primary"><ReceiptLink id={r.id} number={r.number} /> · {dayWords(r.receivedDate)}{all ? ` · ${r.branch.name}` : ""}</div>
                <div className="sub">{countsWords(r.lineCount, r.unitCount)} · {formatMoney(r.totalExVatCents)} ex VAT · {r.purchaseOrderId ? "against an order" : "no purchase order"}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </Panel>
  );
}

// --- Settings › Markup & fitting: recent overrides at receipt (UC-PRICING-007 A6, A7; TC-PRICING-107) ----------------------------

const DAYS = 30;

/**
 * The prices kept at receipts at the acting branch over the last 30 days that differ from their proposal — the receipt's price-history
 * entries (source `Receipt`, every role reads them) with the proposal, the kept price, who and why; the unit's supplier, cost and
 * band come from the receipt's line, read only by the roles that read receipts (`purchasing.po.read`).
 */
/**
 * Every receipt entry of the branch's price history since a day, newest first — every page of it, since the overrides are a
 * filter of those entries and the first page alone would miss any past it (UC-PRICING-007 A6). Bounded: thirty days of one
 * branch's receipts, read 200 at a time.
 */
async function receiptEntriesSince(branchId: string, since: string): Promise<PriceHistoryEntryView[]> {
  const out: PriceHistoryEntryView[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 50; i++) {
    const { data } = await pricing.history({ branchId, source: "Receipt", from: since }, { limit: PRICE_HISTORY_PAGE.limitMax, cursor });
    out.push(...data.items);
    if (!data.nextCursor || data.items.length === 0) break;
    cursor = data.nextCursor;
  }
  return out;
}

export function RecentReceiptOverridesPanel({ branchId, branchName, bands }: { branchId: string | null; branchName: string; bands: readonly FrameMarkupBandView[] }) {
  const s = useSessionView();
  const readsReceipts = can(s, "purchasing.po.read");
  const since = addDays(localDate(new Date()), -DAYS);
  const q = useQuery({
    queryKey: ["pricing", "history", "receipt-overrides", branchId, since],
    queryFn: () => receiptEntriesSince(branchId!, since),
    enabled: branchId !== null,
  });
  const entries = (q.data ?? []).filter((e) => e.proposalCents !== null && e.newValue !== null && e.newValue !== e.proposalCents);
  const receiptIds = readsReceipts ? [...new Set(entries.map((e) => e.reference?.id).filter((x): x is string => !!x))] : [];
  const receipts = useQueries({ queries: receiptIds.map((id) => ({ queryKey: ["goods-receipts", "one", id], queryFn: () => goodsReceipts.get(id), staleTime: 5 * 60_000 })) });
  const receiptOf = (id: string | undefined) => (id ? receipts[receiptIds.indexOf(id)]?.data?.data : undefined);
  /** The receipt's line behind an entry: the line of the unit, of the branch price, or of the product. */
  const subLine = (e: PriceHistoryEntryView): ReactNode => {
    const r = receiptOf(e.reference?.id);
    if (!r) return null;
    const line = r.lines.find((l) => (e.entity === "Serial" ? l.units.some((u) => u.serialId === e.entityId) : e.entity === "BranchPrice" ? l.branchPriceId === e.entityId : l.productId === e.productId));
    if (!line) return <>{r.supplier.name}</>;
    const p = line.proposal;
    const band = p?.bandId ? bands.find((b) => b.id === p.bandId) : undefined;
    const basis = !p || p.marginPercentBp === null ? null
      : p.source === "Band" ? `${band?.scope === "Branch" ? `${possessive(branchName)} band` : band ? "tenant band" : "band"} ${markupText(p.marginPercentBp, p.marginValueCents ?? 0)}`
        : `markup ${markupText(p.marginPercentBp, p.marginValueCents ?? 0)}`;
    return <>{r.supplier.name} · cost <span className="mono nw">{formatMoney(line.unitCostCents)}</span>{basis ? ` · ${basis}` : ""}</>;
  };
  return (
    <Panel title="Recent overrides at receipt" meta={`${branchName} · last ${DAYS} days`} tight
      head={can(s, "governance.control_log.read") ? <Link className="btn sm right" to="/control-log">Control log</Link> : undefined}
      foot={<span className="help">A price kept at a receipt other than its proposal needs a reason; one below the floor of the discount threshold, or one that sets the branch&#39;s price, needs an authoriser&#39;s Control PIN and is in the Control log.</span>}>
      {q.isPending && branchId !== null && <div className="panel-body"><Loading what="the overrides" /></div>}
      {q.isError && <div className="panel-body"><ErrorAlert error={q.error} /></div>}
      {(branchId === null || q.data) && (entries.length === 0 ? <Empty title={`No price was kept apart from its proposal at ${branchName} in the last ${DAYS} days.`} /> : (
        <div className="tbl-scroll">
          <table>
            <thead><tr><th>Date</th><th>Receipt</th><th>Product</th><th className="r">Proposed</th><th className="r">Kept</th><th>By</th><th>Reason</th></tr></thead>
            <tbody>
              {entries.map((e) => {
                const sub = subLine(e);
                return (
                  <tr key={e.id}>
                    <td className="nw">{formatDate(e.changedAt)}</td>
                    <td className="nw"><ReceiptLink id={e.reference?.id} number={e.reference?.number} /></td>
                    <td>{e.product?.name ?? dash}{sub && <div className="small faint">{sub}</div>}</td>
                    <td className="money">{formatMoney(e.proposalCents)}</td>
                    <td className="money strong">{formatMoney(e.newValue)}</td>
                    <td className="nw">{byLine(e.changedBy)}</td>
                    <td>{e.reason ?? dash}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </Panel>
  );
}
